/**
 * The six ink-brush sample moves (C0-VFX; design and rules in docs/research/C0_VFX.md): each a
 * timeline of the caster's motion keys, the foe's reaction and the effect, in four beats —
 * wind-up (a readable tell in the kind's accent), strike (the brush stroke), impact (splatter,
 * the guard cracking), fade (the ink bleeding pale and dissolving). Used by the ink lab
 * (tools/inkLab) and, through render/ink/game.ts, by the game's effects for the moves they replace.
 *
 * Coordinates: art pixels, local to the caster's feet, +x toward the foe (mirrored when the caster
 * faces left), up is −y. The foe stands at (`D`, `tdy`). Everything drawn is a cached stroke frame
 * or a drop sprite (render/ink/brush.ts): a handful of drawImage calls a frame.
 */
import { drawDrop, drawStroke, qiLook, type Pt, type Stroke } from './brush';
import { clamp01, easeIn, easeOut, hash2, span } from './noise';
import { INK_PALETTES, type InkKind, type InkTone } from './palette';

export interface InkScene {
  /** The caster's feet on screen (art pixels) and facing (`flip`: facing left). */
  x: number;
  y: number;
  flip: boolean;
  /**
   * The foe's feet, relative to the caster along the facing (x) and down the screen (y). `D` has a floor (the game's
   * scenes: 28 px) so trails from the caster have room; `Dr` is the real distance, for what lands on the foe.
   */
  D: number;
  Dr?: number;
  tdy: number;
  /** The lab's stage: also dims the ground behind the fighters for the ultimate (the game has its own). */
  lab?: boolean;
  /** The foe guards (the break sample draws the guard ring and its shattering); default true. */
  guard?: boolean;
  /** The foe bleeds on the sample's own hit times (the lab); the game draws blood from real hits instead. */
  blood?: boolean;
  /** The player's blood setting is off (render/blood.setting.ts): a flash and an ink burst instead. */
  noGore?: boolean;
  /**
   * The move kind's tone, when a sample is played for another move (render/ink/kinds.ts): its
   * attack, break and power tones are drawn in this one instead.
   */
  tone?: InkTone;
  /** The ultimate without its painted glyph (a power move whose glyph has no strokes written). */
  noGlyph?: boolean;
}

/** A sample's own tone, or the kind tone it is played in. */
const toned = (kind: InkTone, sc: InkScene): InkTone =>
  sc.tone && (kind === 'attack' || kind === 'break' || kind === 'power' || kind === 'heal') ? sc.tone : kind;
export type InkLayer = 'ground' | 'over';
export type FoePose = 'ready' | 'block' | 'blockHit' | 'hurt';

export interface InkSample {
  /** The six samples, or a style variant (`<sample>-<style>`, render/ink/styles.ts). */
  id: string;
  /** Lab label (Thai, the move's own name where it has one). */
  label: string;
  /** The game move it replaces (null: the basic sword chain). */
  skill: string | null;
  kind: InkKind;
  armed: boolean;
  /** The caster's motions: [motion id, [time s, key index][]] in turn. */
  motions: readonly (readonly [string, readonly (readonly [number, number])[]])[];
  /** The foe's pose over time: [from time, pose]. Empty: no foe (a heal). */
  foe: readonly (readonly [number, FoePose])[];
  /** When the move's blow lands (s), the whole effect's length, and the lab loop. */
  hits: readonly number[];
  life: number;
  loop: number;
  /** Where in this timeline the game's skill event falls (the charge before it is the cast). */
  release: number;
  draw(g: CanvasRenderingContext2D, t: number, sc: InkScene, layer: InkLayer): void;
  // ── the spectacle round (lab stage; lock-on forms and showcases) ──
  /** The weight of each hit's spectacle (render/ink/spectacle.ts); default: the kind's, the basic chain light. */
  weights?: readonly (0 | 1 | 2)[];
  /** The foe's own motion over time (a lock-on form's receive): [motion id, [time, key][]]. */
  foeMotion?: readonly [string, readonly (readonly [number, number])[]];
  /** The foe lifted off the ground (knocked up), and turned round through the views over [t0, t1]. */
  foeLift?: (t: number) => number;
  foeSpin?: readonly [number, number];
  /** The caster lifted (a leap) and pushed forward (a dash), art px. */
  casterLift?: (t: number) => number;
  casterDash?: (t: number) => number;
  /** The caster turned to face left (a warp behind the foe), C0-SKILLFX. */
  casterFlip?: (t: number) => boolean;
  /** Windows of afterimages trailing the caster. */
  ghosts?: readonly (readonly [number, number])[];
  /** How each hit bleeds, drawn by the lab stage (samples that draw their own blood leave it out). */
  bloods?: readonly BloodHit[];
}

// ── helpers ────────────────────────────────────────────────────────────────────

/** Points round an ellipse from angle a0 to a1 (radians, screen: +y down). */
export function arc(cx: number, cy: number, rx: number, ry: number, a0: number, a1: number, n = 10): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const a = a0 + ((a1 - a0) * i) / n;
    out.push([cx + Math.cos(a) * rx, cy + Math.sin(a) * ry]);
  }
  return out;
}

/**
 * The camera's height factor (client0's free camera: figures stand taller at a low pitch, `iso.heightK`, 1 at 30°),
 * set each frame by a render pass (render/ink/lockBind.ts); 1 in the lab.
 */
let HK = 1;
export function setInkHeightK(k: number): void {
  HK = k;
}

/** Where the foe really stands along the facing (local x): what lands on the foe is drawn here. */
export const foeX = (sc: InkScene): number => sc.Dr ?? sc.D;

/**
 * A local point to the screen. Heights above the ground line between the caster's feet and the foe's (0 at the caster,
 * `tdy` at the foe) grow with the camera's height factor, so chest-high effects stay on the chest at any pitch.
 */
export const P = (sc: InkScene, lx: number, ly: number): [number, number] => {
  if (HK === 1) return [sc.x + (sc.flip ? -lx : lx), sc.y + ly];
  const ground = sc.tdy * Math.max(0, Math.min(1, lx / Math.max(1, sc.D)));
  return [sc.x + (sc.flip ? -lx : lx), sc.y + ground + (ly - ground) * HK];
};

/** Draws a stroke whose points are local to the caster (mirrored with the facing). */
export function stroke(
  g: CanvasRenderingContext2D,
  st: Stroke,
  kind: InkTone,
  sc: InkScene,
  reveal: number,
  fade = 0,
  tail = 0,
  ox = 0,
  oy = 0,
): void {
  const [x, y] = P(sc, ox, oy);
  drawStroke(g, st, toned(kind, sc), x, y, reveal, fade, sc.flip, tail);
}

/**
 * Brush slashes and trails (the cut trails, the break's chop, the ultimate's cut, thrust streaks, claw rakes, the warp's
 * dash, the uppercut): OFF by the user's order (2026-10-10 ~22:30, DECISIONS_2026-10-10: "remove the black ink-brush
 * slash pattern"). The rest stays: blood, sprays, rings, the guard shield, palms, qi, heals, dragons, flying swords.
 */
export const BRUSH_SLASHES = false;
/** `stroke` for a brush slash or trail: nothing while `BRUSH_SLASHES` is off. */
export function slashStroke(...a: Parameters<typeof stroke>): void {
  if (BRUSH_SLASHES) stroke(...a);
}

/**
 * How the skill-FX scale pass (lane C0-VFX-PREP, toward the auras/skills refs) grows a template's strokes:
 * `box` scales each stroke about its own box centre (crescents, rings, lotus and rakes grow in place, widths too);
 * `width` only widens it (lines from the caster to the foe stay attached at both ends); `none` keeps it (the
 * ultimate's glyph, the dragons, the showcases: already big).
 */
export type Grow = 'box' | 'width' | 'none';
/** One mode for the whole template, or per top-level key (`'*'` the default). */
export type GrowSpec = Grow | ({ '*': Grow } & Record<string, Grow>);
/** The pass's factors: stroke size, stroke width, and the spread of sprays (drops fly further, a little bigger). */
export const INK_GROW = { size: 1.8, width: 1.5, spray: 1.7, drop: 1.3 } as const;

const isStroke = (v: unknown): v is Stroke =>
  !!v && typeof v === 'object' && Array.isArray((v as Stroke).pts) && typeof (v as Stroke).w === 'number';

function growStroke(st: Stroke, mode: Grow): Stroke {
  const w = st.w * INK_GROW.width;
  if (mode === 'width') return { ...st, w };
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of st.pts) {
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x);
    y1 = Math.max(y1, y);
  }
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  const S = INK_GROW.size;
  return { ...st, w, pts: st.pts.map(([x, y]) => [cx + (x - cx) * S, cy + (y - cy) * S] as Pt) };
}

/** Every stroke inside a built template (objects and arrays walked), grown by `mode`. */
function grown<T>(v: T, mode: Grow): T {
  if (mode === 'none') return v;
  if (isStroke(v)) return growStroke(v, mode) as T;
  if (Array.isArray(v)) return v.map((x) => grown(x, mode)) as T;
  if (v && typeof v === 'object') {
    const o: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v)) o[k] = grown(x, mode);
    return o as T;
  }
  return v;
}

function growAll<T>(v: T, spec: GrowSpec): T {
  if (typeof spec === 'string' || !v || typeof v !== 'object' || Array.isArray(v)) return grown(v, typeof spec === 'string' ? spec : spec['*']);
  const o: Record<string, unknown> = {};
  for (const [k, x] of Object.entries(v)) o[k] = grown(x, spec[k] ?? spec['*']);
  return o as T;
}

/** Strokes are built once per foe distance (rounded), since their points reach the foe; `grow`: the scale pass. */
export function memo<T>(make: (D: number, tdy: number) => T, grow: GrowSpec = 'none'): (sc: InkScene) => T {
  const m = new Map<string, T>();
  return (sc) => {
    const D = Math.round(sc.D / 4) * 4;
    const tdy = Math.round(sc.tdy / 4) * 4;
    const k = `${D},${tdy}`;
    let v = m.get(k);
    if (!v) m.set(k, (v = growAll(make(D, tdy), grow)));
    return v;
  };
}

export interface Splat {
  vx: number;
  vy: number;
  r: number;
  acc: boolean;
}

/** A spray of drops: `n` of them, flung in a cone round `dir` (radians) at `speed` art px/s. */
export function splats(seed: number, n: number, dir: number, cone: number, speed: number, rMax = 2.5, accShare = 0.3): Splat[] {
  return Array.from({ length: n }, (_, i) => {
    const a = dir + (hash2(i, 1, seed) - 0.5) * cone;
    const v = speed * (0.45 + 0.75 * hash2(i, 2, seed));
    return {
      vx: Math.cos(a) * v,
      vy: Math.sin(a) * v,
      r: 1 + (rMax - 1) * hash2(i, 3, seed) ** 2,
      acc: hash2(i, 4, seed) < accShare,
    };
  });
}

/**
 * Draws a spray `tau` seconds after it burst at local (`lx`, `ly`): ballistic, landing on the
 * ground line (local y = `floor`) as stains that dry away over `dryT` seconds.
 */
export function drawSplats(
  g: CanvasRenderingContext2D,
  list: readonly Splat[],
  kind: InkTone,
  sc: InkScene,
  lx: number,
  ly: number,
  tau: number,
  floor: number,
  dryT = 0.5,
  grav = 260,
): void {
  if (tau < 0) return;
  for (let i = 0; i < list.length; i++) {
    const d = list[i]!;
    // Air drag: drops slow as they fly.
    const k = (1 - Math.exp(-3 * tau)) / 3;
    let x = lx + d.vx * k * INK_GROW.spray;
    let y = ly + d.vy * k * INK_GROW.spray + 0.5 * grav * tau * tau;
    let r = d.r * INK_GROW.drop;
    if (y > floor) {
      // The qi look leaves no ink stains: a qi drop is gone when it lands (blood and earth still stain).
      if (qiLook(kind)) continue;
      // Landed: a flattened stain where it fell, drying away.
      const land = tau - 0.25;
      const dry = clamp01(land / dryT);
      if (hash2(i, 9, 3) < dry) continue;
      y = floor - (i % 3);
      r = Math.max(1, d.r * (1 - dry * 0.6));
    } else if (tau > 0.9) {
      if (hash2(i, 9, 5) < (tau - 0.9) * 2) continue;
    }
    x = Math.round(x);
    const [sx, sy] = P(sc, x, 0);
    drawDrop(g, toned(kind, sc), sx, sy + y, r, d.acc ? 'acc' : 'ink');
  }
}

/** Drops drawn in toward a point (qi gathering): from a ring of radius `r0` at 0 to the point at 1. */
export function drawGather(
  g: CanvasRenderingContext2D,
  kind: InkTone,
  sc: InkScene,
  lx: number,
  ly: number,
  k: number,
  n: number,
  r0: number,
  seed: number,
  flat = 1,
): void {
  if (k <= 0 || k >= 1) return;
  for (let i = 0; i < n; i++) {
    const start = hash2(i, 1, seed) * 0.45;
    const u = clamp01((k - start) / (1 - start));
    if (u <= 0 || u >= 1) continue;
    const a = (i / n) * Math.PI * 2 + hash2(i, 2, seed) + u * 2.2;
    const rr = r0 * (1 - easeIn(u)) * (0.8 + 0.4 * hash2(i, 3, seed));
    const [sx, sy] = P(sc, lx + Math.cos(a) * rr, ly + Math.sin(a) * rr * flat);
    drawDrop(g, toned(kind, sc), sx, sy, u > 0.75 ? 1 : i % 3 === 0 ? 2 : 1.5, i % 2 ? 'acc' : 'ink');
  }
}

// ── 1. basic sword chain: ink arc trails ─────────────────────────────────────────

/** A path pushed out by (`dx`, `dy`): the echo behind a trail. */
const shifted = (pts: readonly Pt[], dx: number, dy: number): Pt[] => pts.map(([x, y]) => [x + dx, y + dy] as Pt);

const slashStrokes = memo(() => ({
  // The echo: a wider pale wash stroke a little outside the trail, under it (the layered brush).
  cut1e: {
    pts: shifted([[-16, -78], [2, -90], [24, -80], [38, -58], [40, -36], [30, -18]], 3, -2),
    w: 13,
    shape: 'press',
    body: 'wash',
    dry: 0.75,
    seed: 15,
  } as Stroke,
  cut2e: {
    pts: shifted([[8, -16], [30, -26], [42, -48], [38, -74], [22, -92], [4, -98]], 4, 1),
    w: 13,
    shape: 'press',
    body: 'wash',
    dry: 0.75,
    seed: 16,
  } as Stroke,
  cut1: {
    pts: [
      [-16, -78],
      [2, -90],
      [24, -80],
      [38, -58],
      [40, -36],
      [30, -18],
    ],
    w: 8,
    shape: 'press',
    dry: 0.55,
    seed: 11,
  } as Stroke,
  cut2: {
    pts: [
      [8, -16],
      [30, -26],
      [42, -48],
      [38, -74],
      [22, -92],
      [4, -98],
    ],
    w: 8,
    shape: 'press',
    dry: 0.55,
    seed: 12,
  } as Stroke,
}), 'box');

const SLASH: InkSample = {
  id: 'slash',
  label: 'กระบวนกระบี่พื้นฐาน (ฟันสองจังหวะ)',
  skill: null,
  kind: 'attack',
  armed: true,
  motions: [
    [
      'wxCleave',
      [
        [0, 0],
        [0.06, 1],
        [0.14, 2],
        [0.22, 3],
        [0.27, 3],
        [0.31, 4],
        [0.34, 5],
        [0.4, 6],
        [0.47, 7],
        [0.54, 8],
        [0.6, 9],
      ],
    ],
    [
      'wxRise',
      [
        [0.6, 0],
        [0.66, 1],
        [0.72, 2],
        [0.77, 3],
        [0.8, 4],
        [0.85, 5],
        [0.93, 6],
        [1.02, 7],
        [1.12, 8],
      ],
    ],
  ],
  foe: [
    [0, 'ready'],
    [0.34, 'hurt'],
    [0.52, 'ready'],
    [0.79, 'hurt'],
    [1.0, 'ready'],
  ],
  hits: [0.34, 0.79],
  life: 1.4,
  loop: 1.7,
  release: 0,
  draw(g, t, sc, layer) {
    if (layer !== 'over') return;
    drawCut(g, 1, t - 0.34, sc);
    drawCut(g, 2, t - 0.79, sc);
  },
};

/**
 * One cut of the basic chain at `tau` seconds from the blade meeting the foe: the trail painted
 * just before, the hit's blood on the foe (part 'hit'), the ink fading.
 */
export function drawCut(
  g: CanvasRenderingContext2D,
  which: 1 | 2,
  tau: number,
  sc: InkScene,
  part: 'all' | 'trail' | 'hit' = 'all',
): void {
  if (tau < -0.06 || tau > 0.5) return;
  const S = slashStrokes(sc);
  if (part !== 'hit') {
    slashStroke(g, which === 1 ? S.cut1e : S.cut2e, 'attack', sc, easeOut(span(tau, -0.04, 0.04)), span(tau, 0.05, 0.3));
    slashStroke(g, which === 1 ? S.cut1 : S.cut2, 'attack', sc, easeOut(span(tau, -0.05, 0.02)), span(tau, 0.08, 0.44));
  }
  if (part === 'trail') return;
  drawBlood(g, tau, sc, which === 1 ? 'down' : 'up');
}

// ── 2. break: an amber brush chop that cracks the guard ─────────────────────────

const breakStrokes = memo((D, tdy) => {
  const gx = D - 12;
  const gy = tdy - 44;
  const ring = arc(gx, gy, 9, 22, -Math.PI * 0.62, Math.PI * 1.25, 14);
  return {
    // The ground tell: an amber dry-brush line from the caster's feet to the foe, growing in the wind-up.
    tell: { pts: [[8, 2], [D * 0.5, tdy * 0.5 + 3], [D - 6, tdy + 2]], w: 5, shape: 'swell', body: 'acc', dry: 0.75, seed: 30 } as Stroke,
    // The foe's guard: an ink ring before them (sky halo: the defender's colour).
    guard: { pts: ring, w: 3.5, shape: 'press', dry: 0.35, seed: 31 } as Stroke,
    chop: {
      pts: [
        [-8, -100],
        [18, -98],
        [42, -76],
        [56, -48],
        [58, -22],
        [50, -6],
      ],
      w: 12,
      shape: 'press',
      body: 'acc',
      dry: 0.6,
      seed: 32,
    } as Stroke,
    chopInk: {
      pts: [
        [-4, -96],
        [20, -92],
        [43, -70],
        [55, -44],
        [56, -22],
      ],
      w: 4,
      shape: 'flick',
      dry: 0.5,
      seed: 33,
    } as Stroke,
    cracks: [
      { pts: [[gx - 2, gy - 18], [gx + 3, gy - 8], [gx - 1, gy], [gx + 4, gy + 9]], w: 1.6, shape: 'even', dry: 0, noHalo: true, seed: 34 },
      { pts: [[gx + 1, gy - 2], [gx + 8, gy - 6], [gx + 11, gy - 13]], w: 1.4, shape: 'even', dry: 0, noHalo: true, seed: 35 },
      { pts: [[gx, gy + 3], [gx - 7, gy + 9], [gx - 9, gy + 17]], w: 1.4, shape: 'even', dry: 0, noHalo: true, seed: 36 },
    ] as Stroke[],
    // The guard's pieces: six arcs of the ring, each flying off on its own.
    shards: Array.from({ length: 6 }, (_, i): Stroke => {
      const a0 = -Math.PI * 0.62 + (i * Math.PI * 1.87) / 6;
      return { pts: arc(0, 0, 9, 22, a0, a0 + 0.8, 4).map(([x, y]) => [x, y] as Pt), w: 3.5, shape: 'blunt', dry: 0.2, seed: 40 + i };
    }),
    gx,
    gy,
  };
}, { '*': 'box', tell: 'width', guard: 'none', cracks: 'none', shards: 'none' });
const BREAK_SPRAY = splats(51, 10, -0.2, 2.2, 150, 2, 0.7);
const BREAK_STUN = Array.from({ length: 3 }, (_, i) => i);


// ── the broken guard's shield icon (the user, 2026-10-10) ─────────────────────────

const SHIELD_W = 14;
const SHIELD_H = 16;
/** The crack's column at each row (canvas pixels, two per art pixel): a zigzag down the middle. */
const crackAt = (y: number): number => SHIELD_W + (Math.floor(y / 3) % 2 ? 1 : -1) * (y % 3 === 0 ? 0 : 1) + (y > 18 ? 1 : 0);
const SHIELDS = new Map<string, HTMLCanvasElement>();
/**
 * The shield in amber pixels, two canvas pixels per art pixel: whole, cracked down the middle, or
 * one of its two halves. A heater shield: flat top, straight sides, a point at the bottom, a dark
 * outline, the lit edge upper right and a raised rib down the middle.
 */
function shieldPic(part: 'whole' | 'cracked' | 'left' | 'right'): HTMLCanvasElement {
  let c = SHIELDS.get(part);
  if (c) return c;
  const W = SHIELD_W * 2;
  const H = SHIELD_H * 2;
  c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d')!;
  const P2 = INK_PALETTES.break;
  const inside = (x: number, y: number): boolean => {
    const cx = x + 0.5 - W / 2;
    const yy = y + 0.5;
    if (yy < 1 || yy > H - 0.5) return false;
    const half = yy < H * 0.5 ? W / 2 - 1 : (W / 2 - 1) * Math.sqrt(Math.max(0, 1 - ((yy - H * 0.5) / (H * 0.5)) ** 2));
    return Math.abs(cx) <= half;
  };
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      if (!inside(x, y)) continue;
      const edge = !inside(x - 1, y) || !inside(x + 1, y) || !inside(x, y - 1) || !inside(x, y + 1);
      const edge2 = !inside(x - 2, y) || !inside(x + 2, y) || !inside(x, y - 2) || !inside(x, y + 2);
      const crack = crackAt(y);
      if (part === 'left' && x >= crack) continue;
      if (part === 'right' && x < crack) continue;
      let col = x < W / 2 - 1 ? P2.acc : P2.accLit;
      if (x >= W / 2 - 1 && x <= W / 2) col = P2.accLit;
      else if (x < W / 2 - 1) col = y > H * 0.6 ? P2.accDark : P2.acc;
      if (edge2 && (x > W / 2 && y < H * 0.5)) col = '#fff3c4';
      if (edge) col = '#3a1a06';
      if (part === 'cracked' && (x === crack || x === crack - 1) && y > 1) col = '#3a1a06';
      if ((part === 'left' && x === crack - 1) || (part === 'right' && x === crack)) col = '#3a1a06';
      g.fillStyle = col;
      g.fillRect(x, y, 1, 1);
    }
  SHIELDS.set(part, c);
  return c;
}
const SHIELD_CHIPS = splats(760, 7, -Math.PI / 2, 2.6, 70, 1.5, 1);

/**
 * The shield icon over the broken fighter's head, `tau` seconds after the guard breaks: whole a
 * moment (a white flash round it on the first frames), cracked, then split in two halves that fall
 * apart with amber chips, fading out by about 0.7 s.
 */
export function drawShieldBreak(g: CanvasRenderingContext2D, tau: number, sc: InkScene): void {
  if (tau < 0 || tau > 0.75) return;
  const [x, y] = P(sc, sc.D, sc.tdy - 98);
  const X = Math.round(x - SHIELD_W / 2);
  const Y = Math.round(y - SHIELD_H / 2);
  if (tau < 0.22) {
    if (tau < 0.05) {
      g.fillStyle = '#ffffff';
      g.fillRect(X - 1, Y - 1, SHIELD_W + 2, SHIELD_H + 2);
    }
    g.drawImage(shieldPic(tau < 0.12 ? 'whole' : 'cracked'), X, Y, SHIELD_W, SHIELD_H);
    return;
  }
  const u = tau - 0.22;
  if (u > 0.35) g.globalAlpha = 0.5;
  const dx = 18 * u;
  const dy = 70 * u * u + 6 * u;
  g.drawImage(shieldPic('left'), Math.round(X - dx), Math.round(Y + dy), SHIELD_W, SHIELD_H);
  g.drawImage(shieldPic('right'), Math.round(X + dx), Math.round(Y + dy + 2 * u), SHIELD_W, SHIELD_H);
  g.globalAlpha = 1;
  drawSplats(g, SHIELD_CHIPS, 'break', sc, sc.D, sc.tdy - 98, u, sc.tdy + 2, 0.2, 200);
}

/**
 * The foe's guard breaking under the chop, `tau` seconds after it lands: the ring cracks across,
 * then flies apart in six pieces that fall and fade.
 */
export function drawGuardBreak(g: CanvasRenderingContext2D, tau: number, sc: InkScene): void {
  if (tau < 0 || tau > 0.8) return;
  drawShieldBreak(g, tau, sc);
  if (tau > 0.6) return;
  const S = breakStrokes(sc);
  if (tau < 0.08) {
    stroke(g, S.guard, 'break', sc, 1);
    for (let i = 0; i < S.cracks.length; i++)
      stroke(g, S.cracks[i]!, 'break', sc, easeOut(span(tau, i * 0.015, 0.04 + i * 0.015)));
    return;
  }
  const u = tau - 0.08;
  for (let i = 0; i < S.shards.length; i++) {
    const a = -Math.PI * 0.62 + ((i + 0.5) * Math.PI * 1.87) / 6;
    const v = 70 + 30 * hash2(i, 1, 77);
    const ox = S.gx + Math.cos(a) * (9 + v * u * 0.9) + 25 * u;
    const oy = S.gy + Math.sin(a) * (22 + v * u * 0.5) + 160 * u * u;
    stroke(g, S.shards[i]!, 'break', sc, 1, span(u, 0.12, 0.45), 0, Math.round(ox), Math.round(oy));
  }
}

const BREAK: InkSample = {
  id: 'break',
  label: 'กระบี่ทะลวงปราการ (ท่าทำลาย ทุบการ์ดแตก)',
  skill: 'ns-pierce',
  kind: 'break',
  armed: true,
  motions: [
    [
      'wxBreak',
      [
        [0, 0],
        [0.08, 1],
        [0.2, 2],
        [0.32, 3],
        [0.62, 4],
        [0.68, 5],
        [0.72, 6],
        [0.8, 7],
        [1.0, 8],
        [1.15, 9],
        [1.3, 10],
        [1.45, 11],
      ],
    ],
  ],
  foe: [
    [0, 'block'],
    [0.72, 'blockHit'],
    [0.86, 'hurt'],
    [1.5, 'ready'],
  ],
  hits: [0.72],
  life: 1.6,
  loop: 1.9,
  release: 0.5,
  draw(g, t, sc, layer) {
    const S = breakStrokes(sc);
    if (layer === 'ground') {
      // The tell grows through the wind-up and is wiped as the chop lands.
      stroke(g, S.tell, 'break', sc, easeOut(span(t, 0.12, 0.6)), span(t, 0.74, 0.95));
      return;
    }
    // The guard: painted as the foe raises it, cracked by the chop, then shattered (the lab's
    // foe always guards; in the game the shatter is drawn only when the guard really breaks).
    if (sc.guard !== false) {
      if (t < 0.72) stroke(g, S.guard, 'buff', sc, easeOut(span(t, 0, 0.18)));
      else drawGuardBreak(g, t - 0.72, sc);
    }
    // Wind-up: amber ink drawn up into the raised blade, a wet glint at its tip.
    drawGather(g, 'break', sc, -10, -98, span(t, 0.12, 0.66), 10, 30, 52);
    if (t > 0.3 && t < 0.68) {
      const [x, y] = P(sc, -10, -98);
      drawDrop(g, 'break', x, y, 1.5 + 1.5 * span(t, 0.3, 0.6) + (Math.floor(t * 20) % 2) * 0.5, 'acc');
    }
    // The chop: the amber stroke with an ink twin inside it.
    slashStroke(g, S.chop, 'break', sc, easeOut(span(t, 0.67, 0.74)), span(t, 1.0, 1.5));
    slashStroke(g, S.chopInk, 'break', sc, easeOut(span(t, 0.68, 0.75)), span(t, 0.92, 1.3));
    // Amber chips of the broken guard, then the blow lands: blood.
    drawSplats(g, BREAK_SPRAY, 'break', sc, S.gx, S.gy, t - 0.74, sc.tdy + 2, 0.6);
    if (sc.blood !== false) drawBlood(g, t - 0.76, sc, 'down');
    // Stunned: amber dots circling the foe's head.
    if (t > 0.9 && t < 1.5)
      for (const i of BREAK_STUN) {
        const a = t * 9 + (i * Math.PI * 2) / 3;
        const [x, y] = P(sc, sc.D + Math.cos(a) * 7, sc.tdy - 80 + Math.sin(a) * 2.5);
        drawDrop(g, 'break', x, y, 1.5, i === 0 ? 'acc' : 'ink');
      }
  },
};

// ── 3. palm: an ink burst ───────────────────────────────────────────────────────

const palmStrokes = memo((D, tdy) => {
  const cx = D - 6;
  const cy = tdy - 44;
  return {
    coil: { pts: arc(-8, -38, 6, 5, 0, Math.PI * 3.2, 14), w: 2.5, shape: 'swell', dry: 0.4, seed: 60 } as Stroke,
    rays: Array.from({ length: 9 }, (_, i): Stroke => {
      const a = -Math.PI * 0.75 + (i / 8) * Math.PI * 1.5 + (hash2(i, 1, 61) - 0.5) * 0.25;
      const r0 = 7;
      const r1 = 17 + 9 * hash2(i, 2, 61);
      return {
        pts: [
          [cx + Math.cos(a) * r0, cy + Math.sin(a) * r0],
          [cx + Math.cos(a) * r1, cy + Math.sin(a) * r1],
        ],
        w: 4 + 2 * hash2(i, 3, 61),
        shape: 'flick',
        dry: 0.5,
        seed: 62 + i,
      };
    }),
    ring: { pts: arc(cx, cy, 15, 15, -1.9, -1.9 + Math.PI * 1.85, 16), w: 3.5, shape: 'press', dry: 0.6, seed: 75 } as Stroke,
    core: { pts: [[cx - 3, cy - 3], [cx + 1, cy + 1], [cx + 4, cy + 3]], w: 7, shape: 'drop', body: 'acc', dry: 0.15, seed: 76 } as Stroke,
    // The force carried on through the foe: dry streaks behind them.
    wake: [0, 1, 2].map(
      (i): Stroke => ({
        pts: [
          [cx + 12, cy - 10 + i * 10],
          [cx + 34 + i * 4, cy - 12 + i * 11],
        ],
        w: 3,
        shape: 'flick',
        dry: 0.8,
        seed: 80 + i,
      }),
    ),
    cx,
    cy,
  };
}, 'box');
const PALM_SPRAY = splats(91, 14, 0, 1.6, 170, 3, 0.3);

const PALM: InkSample = {
  id: 'palm',
  label: 'ฝ่ามือคลื่นซัด (ฝ่ามือระเบิดหมึก)',
  skill: 'cp-wave',
  kind: 'attack',
  armed: false,
  motions: [
    [
      'wxPalm',
      [
        [0, 0],
        [0.06, 1],
        [0.14, 2],
        [0.24, 3],
        [0.3, 4],
        [0.33, 5],
        [0.38, 6],
        [0.55, 7],
        [0.72, 8],
        [0.88, 9],
      ],
    ],
  ],
  foe: [
    [0, 'ready'],
    [0.34, 'hurt'],
    [0.75, 'ready'],
  ],
  hits: [0.34],
  life: 1.2,
  loop: 1.5,
  release: 0,
  draw(g, t, sc, layer) {
    if (layer !== 'over') return;
    const S = palmStrokes(sc);
    // Wind-up: qi coiled round the drawn-back palm.
    stroke(g, S.coil, 'attack', sc, span(t, 0.1, 0.28), span(t, 0.3, 0.42));
    // The burst on the foe: a vermilion core, ink rays flung out, the ring painted round.
    stroke(g, S.core, 'attack', sc, easeOut(span(t, 0.33, 0.36)), span(t, 0.46, 0.7));
    for (let i = 0; i < S.rays.length; i++)
      stroke(g, S.rays[i]!, 'attack', sc, easeOut(span(t, 0.33 + i * 0.006, 0.4 + i * 0.006)), span(t, 0.5, 0.86));
    stroke(g, S.ring, 'attack', sc, easeOut(span(t, 0.34, 0.45)), span(t, 0.55, 0.95));
    for (let i = 0; i < S.wake.length; i++)
      stroke(g, S.wake[i]!, 'attack', sc, easeOut(span(t, 0.35, 0.44)), span(t, 0.48, 0.7), span(t, 0.44, 0.6));
    drawSplats(g, PALM_SPRAY, 'attack', sc, S.cx + 6, S.cy, t - 0.34, sc.tdy + 2);
    if (sc.blood !== false) drawBlood(g, t - 0.36, sc, 'spit');
  },
};

// ── 4. qi blast: a spiralling stroke flung to the foe, brush rings on the hit ─────

const qiStrokes = memo((D, tdy) => {
  const x0 = 16;
  const y0 = -48;
  const x1 = D - 4;
  const y1 = tdy - 44;
  const helix = (phase: number, amp: number): Pt[] =>
    Array.from({ length: 33 }, (_, i) => {
      const u = i / 32;
      const a = amp * (1 - 0.45 * u);
      return [x0 + (x1 - x0) * u, y0 + (y1 - y0) * u + Math.sin(u * Math.PI * 5 + phase) * a] as Pt;
    });
  return {
    crescent: { pts: arc(16, -44, 11, 18, -1.2, 1.1, 10), w: 5, shape: 'press', dry: 0.5, seed: 100 } as Stroke,
    spiral: { pts: helix(0, 7), w: 5, shape: 'swell', dry: 0.35, seed: 101 } as Stroke,
    spiral2: { pts: helix(Math.PI, 5), w: 2.5, shape: 'swell', body: 'acc', dry: 0.3, seed: 102 } as Stroke,
    ringA: { pts: arc(x1, y1, 12, 12, -2.4, -2.4 + Math.PI * 1.85, 16), w: 3.5, shape: 'press', dry: 0.5, seed: 103 } as Stroke,
    ringB: { pts: arc(x1, y1, 21, 19, 0.8, 0.8 - Math.PI * 1.7, 18), w: 3, shape: 'press', dry: 0.7, seed: 104 } as Stroke,
    x0,
    y0,
    x1,
    y1,
  };
}, { '*': 'box', spiral: 'width', spiral2: 'width' });
const QI_SPRAY = splats(111, 12, 0, 2.4, 140, 2.5, 0.4);

const QI: InkSample = {
  id: 'qi',
  label: 'ใบไผ่ปลายกระบี่ (ปราณกระบี่เกลียวหมึก)',
  skill: 'bs-gust',
  kind: 'attack',
  armed: true,
  motions: [
    [
      'wxQi',
      [
        [0, 0],
        [0.06, 1],
        [0.14, 2],
        [0.22, 3],
        [0.28, 4],
        [0.32, 5],
        [0.38, 6],
        [0.5, 7],
        [0.66, 8],
        [0.82, 9],
      ],
    ],
  ],
  foe: [
    [0, 'ready'],
    [0.52, 'hurt'],
    [0.9, 'ready'],
  ],
  hits: [0.52],
  life: 1.3,
  loop: 1.6,
  release: 0,
  draw(g, t, sc, layer) {
    if (layer !== 'over') return;
    const S = qiStrokes(sc);
    drawGather(g, 'attack', sc, -8, -34, span(t, 0.06, 0.3), 8, 22, 112);
    stroke(g, S.crescent, 'attack', sc, easeOut(span(t, 0.3, 0.35)), span(t, 0.38, 0.6));
    // In flight: the head runs ahead, the tail dissolving behind it.
    const head = span(t, 0.32, 0.52);
    const tail = Math.max(0, span(t, 0.32, 0.52) - 0.5) + span(t, 0.52, 0.64) * 0.6;
    if (t < 0.7) {
      stroke(g, S.spiral, 'attack', sc, head, span(t, 0.56, 0.7), tail);
      stroke(g, S.spiral2, 'attack', sc, head * 0.98, span(t, 0.54, 0.66), tail);
    }
    if (t > 0.32 && t < 0.53) {
      const [x, y] = P(sc, S.x0 + (S.x1 - S.x0) * head, S.y0 + (S.y1 - S.y0) * head);
      drawDrop(g, 'attack', x, y, 3, 'ink');
      drawDrop(g, 'attack', x, y, 1.5, 'acc');
    }
    stroke(g, S.ringA, 'attack', sc, easeOut(span(t, 0.52, 0.6)), span(t, 0.68, 1.05));
    stroke(g, S.ringB, 'attack', sc, easeOut(span(t, 0.55, 0.66)), span(t, 0.72, 1.15));
    drawSplats(g, QI_SPRAY, 'attack', sc, S.x1, S.y1, t - 0.52, sc.tdy + 2);
    if (sc.blood !== false) drawBlood(g, t - 0.54, sc, 'spit');
  },
};

// ── 5. heal: a jade ink lotus ───────────────────────────────────────────────────

const healStrokes = memo(() => {
  const petal = (a: number, r: number, w: number, body: Stroke['body'], seed: number): Stroke => {
    const c = Math.cos(a);
    const s = Math.sin(a);
    // A petal: from near the centre out, bowed to one side, laid flat on the ground (y halved).
    return {
      // A petal cupped upward: out from the centre on the ground, its tip lifted.
      pts: [
        [c * 3, s * 1.5],
        [c * r * 0.6 - s * 2, s * r * 0.3 - r * 0.15],
        [c * r * 0.85, s * r * 0.42 - r * 0.42],
      ],
      w,
      shape: 'drop',
      body,
      dry: 0.25,
      seed,
    };
  };
  return {
    outer: Array.from({ length: 8 }, (_, i) => petal((i / 8) * Math.PI * 2 + 0.2, 30, 9, 'acc', 120 + i)),
    inner: Array.from({ length: 6 }, (_, i) => petal((i / 6) * Math.PI * 2 + 0.7, 17, 6, 'ink', 130 + i)),
    wisps: [-22, -13, 13, 22].map(
      (x, i): Stroke => ({
        pts: [
          [x, -2],
          [x + (i % 2 ? 4 : -4), -22],
          [x + (i % 2 ? -3 : 3), -44],
          [x + (i % 2 ? 4 : -4), -66],
          [x, -84],
        ],
        w: 3,
        shape: 'swell',
        body: i % 2 ? 'acc' : 'ink',
        dry: 0.5,
        seed: 140 + i,
      }),
    ),
  };
}, 'box');

const HEAL: InkSample = {
  id: 'heal',
  label: 'จิตกระบี่สงบนิ่ง (บัวหมึกหยก)',
  skill: 'ns-calm',
  kind: 'heal',
  armed: true,
  motions: [
    [
      'wxHeal',
      [
        [0, 0],
        [0.12, 1],
        [0.26, 2],
        [0.4, 3],
        [0.6, 4],
        [0.72, 5],
        [0.86, 6],
        [1.0, 7],
        [1.3, 8],
        [1.5, 9],
        [1.65, 10],
        [1.8, 11],
      ],
    ],
  ],
  foe: [],
  hits: [],
  life: 2.0,
  loop: 2.3,
  release: 0,
  draw(g, t, sc, layer) {
    const S = healStrokes(sc);
    if (layer === 'ground') {
      for (let i = 0; i < S.outer.length; i++)
        stroke(g, S.outer[i]!, 'heal', sc, easeOut(span(t, 0.3 + i * 0.05, 0.45 + i * 0.05)), span(t, 1.4 + i * 0.02, 1.9));
      for (let i = 0; i < S.inner.length; i++)
        stroke(g, S.inner[i]!, 'heal', sc, easeOut(span(t, 0.62 + i * 0.05, 0.76 + i * 0.05)), span(t, 1.45, 1.95));
      return;
    }
    // Qi of heaven drawn down into the raised hands, then the ink rising round the body.
    drawGather(g, 'heal', sc, 2, -92, span(t, 0.2, 0.62), 10, 34, 150, 0.6);
    for (let i = 0; i < S.wisps.length; i++)
      stroke(g, S.wisps[i]!, 'heal', sc, span(t, 0.85 + i * 0.06, 1.3 + i * 0.06), span(t, 1.5, 1.85), span(t, 1.15 + i * 0.05, 1.75));
    for (let i = 0; i < 7; i++) {
      const t0 = 0.95 + i * 0.09;
      const u = span(t, t0, t0 + 0.7);
      if (u <= 0 || u >= 1) continue;
      const lx = (hash2(i, 1, 160) - 0.5) * 40;
      const [x, y] = P(sc, lx + Math.sin(u * 6 + i) * 2, -10 - u * 70);
      drawDrop(g, 'heal', x, y, u > 0.7 ? 1 : 1.5, i % 2 ? 'acc' : 'ink');
    }
  },
};

// ── 6. ultimate: the character 斬 painted across the scene, then the great cut ──

/** 斬 on a 24 grid, in stroke order: 車 (seven strokes) then 斤 (four). */
export const ZHAN: readonly { pts: readonly Pt[]; w: number; shape: Stroke['shape'] }[] = [
  { pts: [[1.5, 4], [6, 3.6], [11, 3.4]], w: 2.2, shape: 'press' },
  { pts: [[3, 7], [3.2, 11], [3.4, 14.6]], w: 1.9, shape: 'press' },
  { pts: [[3.3, 7], [7, 6.8], [10, 6.6], [9.9, 10.6], [9.8, 14.6]], w: 2, shape: 'press' },
  { pts: [[3.8, 10.7], [9.4, 10.5]], w: 1.6, shape: 'even' },
  { pts: [[3.8, 14.4], [9.4, 14.2]], w: 1.6, shape: 'even' },
  { pts: [[1, 18.6], [6, 17.8], [11.8, 17]], w: 2.1, shape: 'press' },
  { pts: [[6.5, 1.4], [6.6, 12], [6.6, 23]], w: 2.5, shape: 'press' },
  { pts: [[21.5, 2.4], [17.5, 4.4], [14.6, 5.6]], w: 2.1, shape: 'flick' },
  { pts: [[14.8, 5.6], [14.9, 12], [14.4, 17], [13.4, 21]], w: 2.3, shape: 'press' },
  { pts: [[14.9, 11], [19, 10.6], [22.8, 10.3]], w: 2.1, shape: 'press' },
  { pts: [[19.4, 10.8], [19.5, 17], [19.6, 23.4]], w: 2.5, shape: 'press' },
];

/** Glyph size (art px) and where its top-left sits relative to the midpoint between the fighters. */
const GLYPH = 80;

const ultStrokes = memo((D, tdy) => {
  const u = GLYPH / 24;
  const gx = D / 2 - GLYPH / 2;
  // Above the fighters' heads (about 83 art px tall), so it never covers them.
  const gy = tdy / 2 - GLYPH - 90;
  return {
    glyph: ZHAN.map(
      (s, i): Stroke => ({
        pts: s.pts.map(([x, y]) => [x * u, y * u] as Pt),
        w: s.w * u * 0.95,
        shape: s.shape,
        dry: 0.45,
        seed: 200 + i,
      }),
    ),
    gx,
    gy,
    ring: {
      pts: arc(0, 2, 30, 13, -2.2, -2.2 + Math.PI * 1.9, 18),
      w: 5,
      shape: 'press',
      body: 'acc',
      dry: 0.6,
      seed: 220,
    } as Stroke,
    cut: {
      pts: [
        [D - 58, tdy - 122],
        [D - 22, tdy - 82],
        [D + 4, tdy - 46],
        [D + 26, tdy - 16],
        [D + 46, tdy + 4],
      ],
      w: 15,
      shape: 'press',
      dry: 0.55,
      seed: 221,
    } as Stroke,
    cutCore: {
      pts: [
        [D - 50, tdy - 112],
        [D - 20, tdy - 78],
        [D + 4, tdy - 46],
        [D + 22, tdy - 20],
      ],
      w: 5,
      shape: 'flick',
      body: 'acc',
      dry: 0.3,
      seed: 222,
    } as Stroke,
  };
});
const ULT_SPRAY2 = splats(232, 14, -2.4, 1.6, 150, 3, 0.3);

/** A dither of ink over the stage behind the fighters (the world holding its breath). */
function dim(g: CanvasRenderingContext2D, sc: InkScene, k: number): void {
  if (k <= 0) return;
  g.fillStyle = `rgba(22,17,26,${(0.4 * k).toFixed(3)})`;
  const [cx] = P(sc, sc.D / 2, 0);
  g.fillRect(Math.round(cx - 200), Math.round(sc.y - 220), 400, 260);
}

const ULT: InkSample = {
  id: 'ult',
  label: 'เก้ากระบี่พิฆาตสวรรค์ (ท่าพลัง ตวัดอักษร 斬)',
  skill: 'ns-heaven',
  kind: 'power',
  armed: true,
  motions: [
    [
      'wxUlt',
      [
        [0, 0],
        [0.08, 1],
        [0.18, 2],
        [0.32, 3],
        [0.44, 4],
        [0.54, 5],
        [0.64, 6],
        [0.74, 7],
        [0.84, 8],
        [0.94, 9],
        [1.04, 10],
        [1.14, 11],
        [1.26, 12],
        [1.42, 13],
        [1.48, 14],
        [1.52, 15],
        [1.6, 16],
        [1.85, 17],
        [2.0, 18],
        [2.15, 19],
        [2.3, 20],
        [2.45, 21],
      ],
    ],
  ],
  foe: [
    [0, 'ready'],
    [0.6, 'block'],
    [1.52, 'hurt'],
    [2.3, 'ready'],
  ],
  hits: [1.52],
  life: 2.6,
  loop: 2.9,
  release: 0,
  draw(g, t, sc, layer) {
    const S = ultStrokes(sc);
    if (layer === 'ground') {
      if (sc.lab) dim(g, sc, span(t, 0.45, 0.7) * (1 - span(t, 1.9, 2.4)));
      stroke(g, S.ring, 'power', sc, easeOut(span(t, 0.08, 0.42)), span(t, 0.6, 1.0));
      // The character, painted stroke by stroke behind the fighters (never over them), unmirrored.
      const [gx, gy] = P(sc, sc.D / 2, 0);
      const ox = Math.round(gx - GLYPH / 2);
      const oy = Math.round(gy + (S.gy - 0));
      for (let i = 0; i < (sc.noGlyph ? 0 : S.glyph.length); i++) {
        const t0 = 0.6 + i * 0.055;
        const shatter = span(t, 1.52, 1.95);
        drawStroke(g, S.glyph[i]!, 'power', ox, oy, easeOut(span(t, t0, t0 + 0.07)), Math.max(shatter, span(t, 2.0, 2.4)));
      }
      return;
    }
    drawGather(g, 'power', sc, 0, -40, span(t, 0.05, 0.5), 16, 46, 240);
    slashStroke(g, S.cut, 'power', sc, easeOut(span(t, 1.47, 1.55)), span(t, 1.8, 2.4));
    slashStroke(g, S.cutCore, 'power', sc, easeOut(span(t, 1.48, 1.56)), span(t, 1.7, 2.1));
    if (sc.blood !== false) drawBlood(g, t - 1.53, sc, 'big');
    drawSplats(g, ULT_SPRAY2, 'power', sc, sc.D - 10, sc.tdy - 60, t - 1.56, sc.tdy + 2, 0.7);
  },
};


// ── blood on a hit (the user, 2026-10-10: a sword hit bleeds; no seal stamps) ─────

/**
 * Where the blood of a hit goes, relative to the foe's feet: a cut streak along the blade's path
 * (down: a falling cut, up: a rising cut, big: the ultimate's great cut), or a spit (a palm or qi
 * blow: drops only, no cut).
 */
export type BloodHit = 'down' | 'up' | 'big' | 'spit';

const bloodStrokes = memo((D, tdy) => {
  const cut = (a: Pt, b: Pt, w: number, seed: number): Stroke => ({
    pts: [a, [(a[0] + b[0]) / 2 + 1, (a[1] + b[1]) / 2], b],
    w,
    shape: 'flick',
    body: 'acc',
    dry: 0.15,
    seed,
  });
  const drip = (x: number, y: number, len: number, seed: number): Stroke => ({
    pts: [
      [x, y],
      [x, y + len * 0.5],
      [x + 0.5, y + len],
    ],
    w: 1.5,
    shape: 'even',
    body: 'acc',
    dry: 0,
    seed,
  });
  const x = D;
  const y = tdy;
  return {
    down: cut([x - 9, y - 62], [x + 7, y - 30], 5, 700),
    up: cut([x - 7, y - 28], [x + 7, y - 60], 5, 701),
    big: cut([x - 13, y - 70], [x + 11, y - 18], 7, 702),
    drips: {
      down: [drip(x - 3, y - 52, 9, 710), drip(x + 3, y - 40, 7, 711), drip(x - 1, y - 46, 5, 712)],
      up: [drip(x - 2, y - 36, 8, 713), drip(x + 3, y - 50, 6, 714)],
      big: [drip(x - 6, y - 58, 11, 715), drip(x, y - 44, 9, 716), drip(x + 5, y - 30, 7, 717), drip(x - 3, y - 36, 6, 718)],
      spit: [] as Stroke[],
    },
  };
}, 'box');
const BLOOD_SPRAY = {
  down: splats(720, 12, 0.75, 1.0, 130, 2.5, 0.8),
  up: splats(721, 12, -0.9, 1.0, 130, 2.5, 0.8),
  big: splats(722, 22, 0.2, 2.2, 170, 3, 0.8),
  spit: splats(723, 7, -0.25, 0.9, 110, 2, 0.8),
};

/**
 * A hit's blood `tau` seconds after it lands: the cut opens along the blade's path in two or three
 * reds, drops fly off the way the blade went, short drips run down from the wound, and all of it
 * fades within about 0.7 s (nothing stays on the figure).
 */
export function drawBlood(g: CanvasRenderingContext2D, tau: number, sc: InkScene, hit: BloodHit): void {
  if (tau < 0 || tau > 0.9) return;
  if (sc.noGore) {
    drawCleanHit(g, tau, sc, hit);
    return;
  }
  const B = bloodStrokes(sc);
  if (hit !== 'spit') {
    stroke(g, B[hit], 'blood', sc, easeOut(span(tau, 0, 0.035)), span(tau, 0.32, 0.62));
    for (let i = 0; i < B.drips[hit].length; i++)
      stroke(g, B.drips[hit][i]!, 'blood', sc, span(tau, 0.06 + i * 0.05, 0.42 + i * 0.05), span(tau, 0.5, 0.78));
  }
  const sp = BLOOD_SPRAY[hit];
  drawSplats(g, sp, 'blood', sc, sc.D + 2, sc.tdy - (hit === 'spit' ? 52 : 45), tau, sc.tdy + 2, 0.45);
}

const CLEAN_SPRAY = {
  down: splats(730, 7, 0.75, 1.2, 110, 2, 0),
  up: splats(731, 7, -0.9, 1.2, 110, 2, 0),
  big: splats(732, 12, 0.2, 2.2, 150, 2.5, 0),
  spit: splats(733, 5, -0.25, 1.2, 100, 1.5, 0),
};

/**
 * The hit with blood off: a white flash (a four-point star with an ink outline, two frames) where
 * the blade crossed, and a small burst of ink drops the way it went.
 */
function drawCleanHit(g: CanvasRenderingContext2D, tau: number, sc: InkScene, hit: BloodHit): void {
  const big = hit === 'big' ? 1.6 : 1;
  if (tau < 0.1) {
    const r = Math.round((4 + 50 * tau) * big);
    const [x, y] = P(sc, sc.D + 1, sc.tdy - 46);
    const X = Math.round(x);
    const Y = Math.round(y);
    g.fillStyle = '#1d1820';
    g.fillRect(X - r - 1, Y - 2, r * 2 + 3, 5);
    g.fillRect(X - 2, Y - r - 1, 5, r * 2 + 3);
    g.fillStyle = '#ffffff';
    g.fillRect(X - r, Y - 1, r * 2 + 1, 3);
    g.fillRect(X - 1, Y - r, 3, r * 2 + 1);
    g.fillRect(X - 3, Y - 3, 7, 7);
  }
  // Ink drops with a pale rim (the steel tone): no red at all with blood off.
  drawSplats(g, CLEAN_SPRAY[hit], 'steel', sc, sc.D + 2, sc.tdy - 46, tau, sc.tdy + 2, 0.35);
}

export const INK_SAMPLES: readonly InkSample[] = [SLASH, BREAK, PALM, QI, HEAL, ULT];
export const inkSample = (id: string): InkSample => INK_SAMPLES.find((s) => s.id === id)!;
/** The sample that replaces a game move, by skill id. */
export const inkSampleOf = (skillId: string): InkSample | undefined =>
  SHOWCASES.find((s) => s.skill === skillId) ?? INK_SAMPLES.find((s) => s.skill === skillId);

/** The ultimates' showcases (render/ink/showcase.ts), which replace a sample for their move when registered. */
const SHOWCASES: InkSample[] = [];
export function registerShowcase(s: InkSample): void {
  SHOWCASES.push(s);
}
